import { cn } from "@/lib/utils";

import type { DocumentStat } from "@/api/queries";

/**
 * A question's forward position in the pipeline, read off its *current* status.
 *
 * `questions` on `DocumentStat` is a snapshot — counts per current status, not a
 * history — so "how many have reached stage N" is reconstructed as "total minus
 * whoever is still sitting at an earlier stage", not read off directly. `failed` gets
 * order 0: a failed row was extracted (the Question row exists), so it still counts
 * there, but we cannot say which later stage it failed *at*, so it stops counting
 * for every stage after that rather than guess. The dedicated "Failed" branch below
 * carries the true total instead.
 */
const STAGE_ORDER: Record<string, number> = {
  extracted: 0,
  failed: 0,
  split_checked: 1,
  enriched: 2,
  embedded: 3,
  dedup_checked: 4,
  pending_review: 5,
  stale: 5,
  duplicate: 5,
  approved: 6,
  rejected: 6,
};

function reached(questions: Record<string, number>, stageIndex: number, total: number): number {
  let before = 0;
  for (const [status, n] of Object.entries(questions)) {
    if ((STAGE_ORDER[status] ?? -1) < stageIndex) before += n;
  }
  return Math.max(total - before, 0);
}

type Node = { label: string; done: number; total: number; sub?: string };

/**
 * The automated zone (DESIGN §6/§7: extraction → enrichment → embedding → dedup →
 * clustering) plus the human-review fork it feeds into, and the two branches that
 * can peel off before reaching it — a duplicate folded in at clustering, a failure
 * at any stage along the way.
 */
export function PipelineStepper({
  doc,
}: {
  doc: Pick<DocumentStat, "chunks" | "chunk_total" | "questions" | "question_total"> &
    Partial<Pick<DocumentStat, "split_rewritten" | "split_added">>;
}) {
  const { chunks, chunk_total, questions, question_total } = doc;
  const rewritten = doc.split_rewritten ?? 0;
  const added = doc.split_added ?? 0;
  const chunksDone = chunk_total - (chunks.raw ?? 0);
  const duplicate = questions.duplicate ?? 0;
  const failed = questions.failed ?? 0;
  const enteredReview = reached(questions, 5, question_total) - duplicate;
  const waitingNow = (questions.pending_review ?? 0) + (questions.stale ?? 0);
  const approved = questions.approved ?? 0;
  const rejected = questions.rejected ?? 0;

  const nodes: Node[] = [
    {
      label: "Ingested",
      done: chunksDone,
      total: chunk_total,
      ...(chunks.failed ? { sub: `${chunks.failed} chunk(s) failed` } : {}),
    },
    {
      label: "Extracted",
      done: question_total,
      total: question_total,
      sub: `${question_total - added} question${question_total - added === 1 ? "" : "s"}`,
    },
    {
      label: "Split",
      done: reached(questions, 1, question_total),
      total: question_total,
      // DESIGN §17.11.4: "10 extracted · 2 split → 12 for review" — the rows a split adds are real
      // questions, so the totals after this step are larger than the one before it.
      ...(added > 0 ? { sub: `${rewritten} split → +${added}` } : {}),
    },
    { label: "Enriched", done: reached(questions, 2, question_total), total: question_total },
    { label: "Embedded", done: reached(questions, 3, question_total), total: question_total },
    { label: "Deduped", done: reached(questions, 4, question_total), total: question_total },
    { label: "Clustered", done: reached(questions, 5, question_total), total: question_total },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-start">
        {nodes.map((n, i) => (
          <StageNode key={n.label} node={n} first={i === 0} />
        ))}
        <div className="flex w-7 flex-none items-center justify-center pt-[5px]">
          <span className="border-bank-border-strong h-[30px] border-l border-dashed" aria-hidden="true" />
        </div>
        <div
          tabIndex={0}
          aria-describedby="stage-tip-Pending-Review"
          className="group focus-visible:ring-bank-accent relative flex-1 cursor-help rounded-md text-center focus-visible:ring-2 focus-visible:outline-none"
        >
          <StageTip label="Pending Review" align="end" />
          <span className="bg-bank-attention mx-auto mb-2 block size-[15px] rounded-full border-[2.5px] border-bank-attention" />
          <p className="text-sm font-semibold">Pending Review</p>
          <p className="text-bank-muted font-mono text-xs tabular-nums">
            {enteredReview} reached · {waitingNow} waiting now
          </p>
          <p className="text-bank-muted mt-0.5 text-[10.5px]">forks to approved / rejected</p>
        </div>
      </div>

      <div className="border-bank-border flex flex-wrap items-center gap-2 border-t border-dashed pt-3.5">
        <Branch tone="dup" label="Merged as duplicate" value={duplicate} />
        <Branch tone="bad" label="Failed (any automated stage)" value={failed} />
        <Branch tone="good" label="Approved" value={approved} />
        <Branch tone="inert" label="Rejected" value={rejected} />
        <span className="text-bank-muted ml-auto text-[11px]">
          A human decision, tracked the same way as a pipeline stage.
        </span>
      </div>
    </div>
  );
}

/**
 * The same journey as `PipelineStepper`, for the questions an approved split sent back through it
 * (DESIGN §17.11.6): they are already extracted and split-checked, so it starts at "Rewritten" and
 * ends at the review queue. `questions` is a count per current status, as on a `DocumentStat`.
 */
export function SplitStepper({ questions, total }: { questions: Record<string, number>; total: number }) {
  const failed = questions.failed ?? 0;
  const inReview = (questions.pending_review ?? 0) + (questions.stale ?? 0);
  const approved = questions.approved ?? 0;
  const nodes: Node[] = [
    { label: "Rewritten", done: total, total },
    // The same stage names the ingest run page uses, so "Enriched" is recognisably the same step.
    { label: "Enriched", done: reached(questions, 2, total), total },
    { label: "Embedded", done: reached(questions, 3, total), total },
    { label: "Deduped", done: reached(questions, 4, total), total },
    { label: "Clustered", done: reached(questions, 5, total), total },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-start">
        {nodes.map((n, i) => (
          <StageNode key={n.label} node={n} first={i === 0} last={i === nodes.length - 1} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Branch tone="attention" label="Waiting for review" value={inReview} />
        <Branch tone="good" label="Approved" value={approved} />
        <Branch tone="dup" label="Merged as duplicate" value={questions.duplicate ?? 0} />
        <Branch tone="bad" label="Failed" value={failed} />
      </div>
    </div>
  );
}

/**
 * What each stage does, in plain words — shown on hover or keyboard focus (DESIGN §6, §7, §17.11).
 * `ai` says whether the stage spends model quota and how much, because "why is this slow?" and "why
 * did my daily allowance go?" are the two questions a person watching this track actually has.
 */
const STAGE_HELP: Record<string, { what: string; ai: string }> = {
  Ingested: {
    what: "The uploaded file is cut into pieces small enough for the AI to read in one go.",
    ai: "No AI — this happens the moment you upload.",
  },
  Extracted: {
    what: "The AI reads each piece and pulls out the individual questions (and their answers, if the document had any).",
    ai: "AI: one request per piece of the document.",
  },
  Split: {
    what: "Checks whether each question is short enough to be read aloud. One that asks two things is rewritten as a short question plus a linked follow-up; short questions pass through untouched.",
    ai: "AI: one request per batch of long questions only.",
  },
  Rewritten: {
    what: "The question was reworded when you approved it, and its follow-up was created. Both now go back through the steps to the right, like any new question.",
    ai: "No AI here — the wording was proposed earlier and you approved it.",
  },
  Enriched: {
    what: "The AI tags each question — topic, type, difficulty, technology — and writes a model answer and a grading checklist. A rewritten question is tagged again, so its difficulty reflects its new, shorter wording.",
    ai: "AI: one request per batch of 10 questions.",
  },
  Embedded: {
    what: "The AI turns what the question is really testing into a numeric fingerprint, so differently worded questions can be compared fairly.",
    ai: "AI: one embedding request per batch.",
  },
  Deduped: {
    what: "The question is compared with everything already in the bank — identical text first, then similar wording, then similar meaning. An AI judge decides only the pairs that are still unclear.",
    ai: "Mostly no AI; a request only for unclear pairs.",
  },
  Clustered: {
    what: "Questions found to be duplicates are grouped under one main version. Whatever is left goes to the review queue for a person.",
    ai: "No AI — pure grouping.",
  },
  "Pending Review": {
    what: "Waiting for a person to approve, edit or reject. Nothing automatic moves a question past this point.",
    ai: "A human decision.",
  },
};

type TipAlign = "start" | "center" | "end";

/** A hover/focus popover. CSS only (`group` on the parent), so it needs no state and works from the
 * keyboard; it opens below the stage and is pinned to the left or right edge for the end nodes so it
 * cannot run off the panel. */
function StageTip({ label, align }: { label: string; align: TipAlign }) {
  const help = STAGE_HELP[label];
  if (!help) return null;
  return <Tip id={`stage-tip-${label.replace(/\s+/g, "-")}`} title={label} {...help} align={align} />;
}

/** The popover itself, shared by the stage track and the Ingest page's job buttons and stage names. */
export function Tip({
  id,
  title,
  what,
  ai,
  align,
}: {
  id: string;
  title: string;
  what: string;
  ai: string;
  align: TipAlign;
}) {
  return (
    <span
      role="tooltip"
      id={id}
      className={cn(
        "border-bank-border-strong bg-bank-surface text-bank-ink pointer-events-none invisible absolute top-full z-30 mt-2 w-64 rounded-md border px-3 py-2 text-left text-xs leading-relaxed font-normal opacity-0 shadow-lg transition-opacity",
        "group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100",
        align === "start" && "left-0",
        align === "end" && "right-0",
        align === "center" && "left-1/2 -translate-x-1/2",
      )}
    >
      <strong className="mb-0.5 block text-[13px] font-semibold">{title}</strong>
      {what}
      <span className="text-bank-muted mt-1.5 block font-mono text-[10.5px]">{ai}</span>
    </span>
  );
}

/** A stage name inside running text that explains itself on hover or focus — for prose that lists the
 * pipeline's stages (the Ingest page's upload note) so the words are not a bare list of jargon. */
export function StageTerm({ label, text }: { label: keyof typeof STAGE_HELP; text?: string }) {
  return (
    <span
      tabIndex={0}
      aria-describedby={`stage-tip-${label.replace(/\s+/g, "-")}-inline`}
      className="group focus-visible:ring-bank-accent relative cursor-help rounded-sm underline decoration-dotted underline-offset-2 focus-visible:ring-2 focus-visible:outline-none"
    >
      {text ?? label}
      <Tip
        id={`stage-tip-${label.replace(/\s+/g, "-")}-inline`}
        title={label}
        {...STAGE_HELP[label]!}
        align="start"
      />
    </span>
  );
}

function StageNode({ node, first, last = false }: { node: Node; first: boolean; last?: boolean }) {
  const complete = node.total > 0 && node.done >= node.total;
  const active = node.done > 0 && node.done < node.total;
  return (
    <div
      tabIndex={0}
      aria-describedby={`stage-tip-${node.label.replace(/\s+/g, "-")}`}
      className="group focus-visible:ring-bank-accent relative flex-1 cursor-help rounded-md text-center focus-visible:ring-2 focus-visible:outline-none"
    >
      <StageTip label={node.label} align={first ? "start" : last ? "end" : "center"} />
      {!first && (
        <span
          className={cn(
            "absolute top-[5.5px] right-1/2 left-[-50%] h-[2px]",
            complete ? "bg-bank-good/55" : "bg-bank-border",
          )}
          aria-hidden="true"
        />
      )}
      <span
        className={cn(
          "relative z-10 mx-auto mb-2 block size-[15px] rounded-full border-[2.5px]",
          complete
            ? "border-bank-good bg-bank-good"
            : active
              ? "border-bank-pipe bg-bank-surface"
              : "border-bank-inert bg-bank-surface",
        )}
      />
      <p className="text-sm font-semibold">{node.label}</p>
      <p
        className={cn(
          "font-mono text-xs tabular-nums",
          active ? "text-bank-pipe-ink font-semibold" : "text-bank-muted",
        )}
      >
        {node.done}/{node.total}
      </p>
      {node.sub && <p className="text-bank-muted mt-0.5 text-[10.5px]">{node.sub}</p>}
    </div>
  );
}

const BRANCH_TONE = {
  attention: "bg-bank-attention-wash text-bank-attention-ink",
  dup: "bg-bank-dup-wash text-bank-dup-ink",
  bad: "bg-bank-bad-wash text-bank-bad-ink",
  good: "bg-bank-good-wash text-bank-good-ink",
  inert: "bg-bank-inert-wash text-bank-inert-ink",
} as const;

function Branch({ tone, label, value }: { tone: keyof typeof BRANCH_TONE; label: string; value: number }) {
  if (value === 0) return null;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs", BRANCH_TONE[tone])}>
      {label} <span className="font-mono font-semibold tabular-nums">{value}</span>
    </span>
  );
}
